import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { getEnvironmentConfig } from "../../utils/environment";
import { CCW_BOOKING_FLOW_UTM } from "../../config/p1SiteSubscriptions.au";
import { getSubscriptionDisplayCopy } from "../../config/ccwSubscriptionCopy";
import ConciergeLogo from "../../assets/images/ccw_logo_white.png";
import "./Subscriptions.css";

const Subscriptions = () => {
  const envCfg = getEnvironmentConfig();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingToggle, setPricingToggle] = useState("monthly"); // "monthly" or "locked"
  const [bookingData, setBookingData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [cartData, setCartData] = useState(null); // Store cart data separately
  const [bookingLocation, setBookingLocation] = useState(null);
  const [resolvedSiteId, setResolvedSiteId] = useState(null);
  const [locationName, setLocationName] = useState(null);
  const pageViewTrackedRef = useRef(false);

  const returnUrl = searchParams.get("returnUrl");
  const bookingId = searchParams.get("booking");
  const siteIdFromUrl = searchParams.get("siteId");

  const trackP1Event = useCallback(async (eventType, extra = {}) => {
    const siteId =
      extra.siteId ??
      resolvedSiteId ??
      siteIdFromUrl ??
      bookingData?.idLocation?.posId ??
      bookingLocation?.posId;
    if (!siteId) return;

    try {
      await api("booking-flow/p1-subscription/track", "POST", {
        eventType,
        siteId,
        bookingId: bookingId || null,
        ...extra,
      });
    } catch (err) {
      console.error("Error tracking P1 subscription event:", err);
    }
  }, [
    resolvedSiteId,
    siteIdFromUrl,
    bookingData,
    bookingLocation,
    bookingId,
  ]);

  useEffect(() => {
    if (!siteIdFromUrl) return;

    const resolveLocationFromUrl = async () => {
      try {
        const response = await api(
          `booking-flow/location-by-site/${siteIdFromUrl}`,
          "GET"
        );
        if (response.status === 200 && response.data.success) {
          const { posId, seoName, name } = response.data.location;
          setResolvedSiteId(posId);
          setLocationName(seoName || name);
        }
      } catch (err) {
        console.error("Error resolving location from siteId:", err);
      }
    };

    resolveLocationFromUrl();
  }, [siteIdFromUrl]);

  useEffect(() => {
    if (siteIdFromUrl || !bookingData) return;

    const posId = bookingData.idLocation?.posId || bookingLocation?.posId;
    const seoName =
      bookingData.idLocation?.seoName ||
      bookingLocation?.seoName ||
      cartData?.step1Data?.selectedLocation?.seoName;
    if (posId) {
      setResolvedSiteId(posId);
      if (seoName) setLocationName(seoName);
    }
  }, [siteIdFromUrl, bookingData, bookingLocation, cartData]);

  useEffect(() => {
    if (!resolvedSiteId || pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    trackP1Event("page_view", { siteId: resolvedSiteId });
  }, [resolvedSiteId, trackP1Event]);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId]);

  const fetchBookingData = async () => {
    try {
      const response = await api(`booking-flow/booking/${bookingId}`, "GET");
      if (response.status === 200 && response.data.success) {
        setBookingData(response.data.booking);
        setCartData(response.data.cart);
        setBookingLocation(response.data.location || null);
        setCustomerData(
          response.data.cart?.step3Data || response.data.cart?.step1Data
        );
      }
    } catch (error) {
      console.error("Error fetching booking data:", error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api("subscription/get-all", "GET");

      if (response.status === 200 && response.data.success) {
        setSubscriptions(response.data.subscriptions);
      } else {
        setError("Failed to load subscriptions");
      }
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Error loading subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToExtras = () => {
    if (returnUrl) {
      // Check if coming from extras page
      if (returnUrl.includes("/booking-extras")) {
        window.location.href = returnUrl;
      } else {
        // Coming from SMS or other source, go to manage booking
        navigate("/");
      }
    } else if (bookingId) {
      // Has booking ID but no return URL, go to manage booking
      navigate(`/manage-booking/${bookingId}`);
    } else {
      // No context, go to home
      navigate("/");
    }
  };

  const generateSubscriptionUrl = (subscription) => {
    // Use the externalLink from the subscription product collection
    let baseUrl = subscription.externalLink;

    if (!baseUrl) {
      console.warn(
        `No externalLink found for subscription: ${subscription.name}`
      );
      return "#"; // Fallback to prevent broken links
    }

    // Always append the product ID based on pricing toggle selection
    if (pricingToggle === "locked") {
      // Use locked product ID (12-month commitment)
      if (
        subscription.stripeLockedId &&
        subscription.stripeLockedId.trim() !== ""
      ) {
        baseUrl = `${baseUrl}${subscription.stripeLockedId}`;
      } else {
        return "#"; // Fallback to prevent broken links
      }
    } else {
      // Use monthly product ID
      if (subscription.stripeId && subscription.stripeId.trim() !== "") {
        baseUrl = `${baseUrl}${subscription.stripeId}`;
      } else {
        return "#"; // Fallback to prevent broken links
      }
    }

    const params = new URLSearchParams();
    const effectiveSiteId =
      resolvedSiteId ||
      siteIdFromUrl ||
      bookingData?.idLocation?.posId ||
      bookingLocation?.posId;
    const effectiveLocationName =
      locationName ||
      bookingData?.idLocation?.seoName ||
      bookingLocation?.seoName ||
      cartData?.step1Data?.selectedLocation?.seoName ||
      bookingData?.idLocation?.name ||
      bookingLocation?.name;

    if (customerData || bookingId || bookingData) {
      const fallbackName = bookingData?.posResponse?.BookingName || "";
      const [fallbackFirst, ...restName] = fallbackName.split(" ");
      const fallbackLast = restName.join(" ") || "";

      const email =
        customerData?.email || bookingData?.posResponse?.BookingEmail || "";
      const phone =
        customerData?.phone || bookingData?.posResponse?.BookingPhone || "";
      const firstName = customerData?.firstName || fallbackFirst || "";
      const lastName = customerData?.lastName || fallbackLast || "";

      if (email) params.append("email", email);
      if (phone) params.append("phone", phone);
      if (firstName) params.append("firstName", firstName);
      if (lastName) params.append("lastName", lastName);

      if (bookingId) params.append("booking", bookingId);

      const vehicleType =
        cartData?.step1Data?.vehicleType ||
        customerData?.vehicleType ||
        bookingData?.vehicleType;

      if (vehicleType) {
        const vehicleTypeMap = {
          Sedan: "sedan",
          "Sedan/Hatch": "sedan",
          Hatch: "sedan",
          Wagon: "wagon",
          SUV: "wagon",
          "Wagon/SUV": "wagon",
          "Wagon / SUV": "wagon",
          "SUV/Wagon": "wagon",
          "4WD": "4wd",
          "4WD/7Seater": "4wd",
          "4WD/7 Seater": "4wd",
          "4WD / 7 Seater": "4wd",
          "7Seater": "4wd",
          "7 Seater": "4wd",
        };
        const normalizedVehicleType =
          vehicleTypeMap[vehicleType] || vehicleType.toLowerCase();
        params.append("vehicleType", normalizedVehicleType);
      }

      const fullYearRRP =
        pricingToggle === "locked"
          ? subscription.lockedPrice * 12
          : subscription.monthlyPrice * 12;
      params.append("rrp", fullYearRRP.toString());

      const webhookUrl = `${envCfg.api.apiUrl}booking-flow/subscription-webhook`;
      params.append("webhookUrl", webhookUrl);
    }

    if (effectiveSiteId) {
      params.append("siteId", String(effectiveSiteId));
      if (effectiveLocationName) {
        params.append("locationName", effectiveLocationName);
      }
    }

    const utmKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ];
    for (const key of utmKeys) {
      const fromPage = searchParams.get(key);
      if (fromPage) {
        params.set(key, fromPage);
      } else if (CCW_BOOKING_FLOW_UTM[key]) {
        params.set(key, CCW_BOOKING_FLOW_UTM[key]);
      }
    }

    if (params.toString()) {
      const separator = baseUrl.includes("?") ? "&" : "?";
      baseUrl = `${baseUrl}${separator}${params.toString()}`;
    }

    return baseUrl;
  };

  const trackSignUpClick = (subscriptionName) => {
    const effectiveSiteId =
      resolvedSiteId ||
      siteIdFromUrl ||
      bookingData?.idLocation?.posId ||
      bookingLocation?.posId;

    if (!effectiveSiteId) return;

    trackP1Event("sign_up_click", {
      siteId: effectiveSiteId,
      subscriptionName,
    }).catch(() => {});
  };

  const handleSignUpLinkClick = (event, subscription, signupUrl) => {
    if (!signupUrl || signupUrl === "#") {
      event.preventDefault();
      return;
    }
    trackSignUpClick(subscription.name);
  };

  const getPrice = (subscription) => {
    return pricingToggle === "locked"
      ? subscription.lockedPrice
      : subscription.monthlyPrice;
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  // Sort subscriptions by price (cheapest to most expensive)
  const sortedSubscriptions = [...subscriptions].sort((a, b) => {
    const priceA = pricingToggle === "locked" ? a.lockedPrice : a.monthlyPrice;
    const priceB = pricingToggle === "locked" ? b.lockedPrice : b.monthlyPrice;
    return priceA - priceB;
  });

  if (loading) {
    return (
      <div className="subscriptions-container">
        <div className="subscriptions-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading subscription plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscriptions-container">
        <div className="subscriptions-content">
          <div className="error-message">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <button onClick={handleBackToExtras} className="retry-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscriptions-container">
      <div className="subscriptions-content">
        {/* Header */}
        <div className="subscriptions-header">
          <img src={ConciergeLogo} alt="Concierge Car Wash" className="logo" />
          <h1>Unlimited Wash Club</h1>
          <p className="subtitle">
            Choose the perfect plan for your car care needs
          </p>
          {locationName && (
            <p className="subtitle location-context">
              Signing up from {locationName}
            </p>
          )}

          {/* Powered By Section */}
          <div className="powered-by-section">
            <p className="powered-by-text">
              Subscriptions powered by{" "}
              <a
                href="https://priorityonewashclub.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="priority-one-link"
              >
                Priority One Wash Club
              </a>
            </p>
          </div>

          {/* Pricing Toggle */}
          <div className="pricing-toggle">
            <button
              className={`toggle-btn ${
                pricingToggle === "monthly" ? "active" : ""
              }`}
              onClick={() => setPricingToggle("monthly")}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${
                pricingToggle === "locked" ? "active" : ""
              }`}
              onClick={() => setPricingToggle("locked")}
            >
              12-Month Lock-In
            </button>
          </div>
        </div>

        {/* Subscriptions Grid */}
        <div className="subscriptions-grid">
          {sortedSubscriptions.map((subscription) => {
            const display = getSubscriptionDisplayCopy(subscription);
            return (
              <div
                key={subscription._id}
                className={`p1-subscription-card ${
                  display.popular ? "popular" : ""
                }`}
              >
              <div className="p1-subscription-header">
                <h3 className="p1-subscription-name">{subscription.name}</h3>
                <div className="p1-subscription-price">
                  <span className="p1-price-amount">
                    {formatPrice(getPrice(subscription))}
                  </span>
                  <span className="p1-price-period">
                    {pricingToggle === "locked"
                      ? "/month (12-month)"
                      : "/month"}
                  </span>
                </div>
              </div>

              <div className="p1-subscription-features">
                <h4>{display.includesFrom}</h4>
                <ul>
                  {display.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
                {display.description && (
                  <p className="p1-subscription-description">
                    {display.description}
                  </p>
                )}
              </div>

              <div className="p1-subscription-actions">
                {(() => {
                  const signupUrl = generateSubscriptionUrl(subscription);
                  return (
                    <a
                      href={signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p1-signup-btn"
                      onClick={(event) =>
                        handleSignUpLinkClick(event, subscription, signupUrl)
                      }
                    >
                      Sign Up
                    </a>
                  );
                })()}
              </div>

              {display.popular && (
                <div className="p1-popular-badge">Most Popular</div>
              )}
              </div>
            );
          })}
        </div>

        {/* Back Button */}
        <div className="back-section">
          <button className="back-btn" onClick={handleBackToExtras}>
            {returnUrl && returnUrl.includes("/booking-extras")
              ? "← Back to Extras"
              : bookingId
              ? "← Manage Booking"
              : "← Back to Home"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
